import useAuth from '@utils/hooks/useAuth'
import { initializeApp } from 'firebase/app'
import { getAuth, signInAnonymously, signOut } from 'firebase/auth'
import { getDatabase, off, onValue, ref } from 'firebase/database'
import { useEffect, useState } from 'react'

interface IFireBaseData {
  initApp: string | null
  notifications: string | null
  company_switch: string | null
}

const useFirebaseListener = (): IFireBaseData => {
  const { isLoggedIn, user } = useAuth()
  const [data, setData] = useState<IFireBaseData>({
    initApp: null,
    notifications: null,
    company_switch: null,
  })
  const firebaseConfigEnv =
    process.env.REACT_APP_FIREBASE_CONFIG &&
    JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG)

  const app = initializeApp(firebaseConfigEnv)
  const auth = getAuth(app)

  useEffect(() => {
    let userDataRef: any
    let companySwitchRef: any
    let userDataUnsubscribe: (() => void) | null = null
    let companySwitchUnsubscribe: (() => void) | null = null

    const setupFirebaseListener = async () => {
      try {
        await signInAnonymously(auth)
        const database = getDatabase(app)

        // Listen for user data
        userDataRef = ref(database, `/users/${user.uid}`)
        userDataUnsubscribe = onValue(
          userDataRef,
          (snapshot) => {
            const userData = snapshot.val()
            if (userData) {
              setData((prevData) => ({
                ...prevData,
                initApp: userData?.initApp,
                notifications: userData?.notifications,
              }))
            } else null
          },
          () => null
        )

        // Listen for company_switch using user.eid
        if (user.eid) {
          companySwitchRef = ref(database, `/users/${user.eid}/company_switch`)
          companySwitchUnsubscribe = onValue(
            companySwitchRef,
            (snapshot) => {
              const companySwitchValue = snapshot.val()
              if (companySwitchValue !== null) {
                setData((prevData) => ({
                  ...prevData,
                  company_switch: companySwitchValue,
                }))
              }
            },
            () => null
          )
        } else {
          // eslint-disable-next-line no-console
          console.log('User EID not available')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error setting up Firebase listener:', error)
      }
    }

    if (isLoggedIn) {
      setupFirebaseListener()
    } else {
      setData({
        initApp: null,
        notifications: null,
        company_switch: null,
      })
      if (auth.currentUser) {
        signOut(auth)
          .then(() => {
            // eslint-disable-next-line no-console
            console.log('fr User logged out successfully')
          })
          .catch((error) => {
            console.error('Error logging out:', error)
          })
      }
    }

    return () => {
      if (userDataUnsubscribe) userDataUnsubscribe()
      if (companySwitchUnsubscribe) companySwitchUnsubscribe()
      if (userDataRef) off(userDataRef)
      if (companySwitchRef) off(companySwitchRef)
    }
  }, [isLoggedIn, user, auth, app])

  return data
}

export default useFirebaseListener
